import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import type { FC } from "react";
import { IBulkExportContextAction, IBulkExportContextState, useBulkExportContext, useSubscriptionProviderContext } from "../../providers";
import { createSteps } from "./BulkExportStepper.utils";
import { useStyles } from "./BulkExportStepper.styles";
import { Step } from "./Step";
import { BulkExportType, IBulkExportStepper } from "./BulkExportStepper.types";
import { useFavouriteProviderContext } from "../../../bulkexport/providers/FavouriteProvider/FavouriteProvider.context";
import { useDataSourcesQuery, useDimensionssQuery, useSavedExportSettingsQuery } from "../../api";
import { useFavouriteItemsQuery } from "../../../favourites/api";
import { BulkExportFormat, IDimension } from "../../types";
import { isEqual } from "lodash";
import { compareExeptions, virtualConfiguration } from "./BulkExportStepper.constants";
import { useBulkExportFavouriteQueryParams } from "../../shared/hooks/useBulkExportFavouriteQueryParams";

export const BulkExportStepper: FC<IBulkExportStepper> = ({ item, bulkType }) => {
  const classes = useStyles();

  const { favoriteBulkId, favoriteSavedId } = useBulkExportFavouriteQueryParams();

  const { savedExportSettings } = useSavedExportSettingsQuery();
  const { dataSources } = useDataSourcesQuery();
  const { data: dataFavouriteItems = [] } = useFavouriteItemsQuery();
  const isSubscriptionBulk = bulkType === BulkExportType.Subscription;
  const itemCubeId = item && "cubeId" in item ? item?.cubeId : undefined;

  const context = useBulkExportContext();
  const contextFavourites = useFavouriteProviderContext();
  const contextSubscription = useSubscriptionProviderContext();
  const initialPayload = contextSubscription?.state?.initialPayload;
  const applyPayload = contextSubscription?.actions?.applyPayload;

  const setCurrentFavouriteItem = contextFavourites?.actions?.setCurrentFavouriteItem;

  const {
    setSavedExportSettingsId,
    setCurrentFavouriteSettings,
    setTab,
    setDataSourceId,
    setSettingsModifiedForFavourites,
    setDimensionCurrentFavourite,
    setDefaultCalendarDimension,
    setCubeId,
  } = context?.actions as IBulkExportContextAction;
  const {
    dataSourceId,
    tab,
    savedExportSettingsId,
    currentFavouriteSettings,
    dimensions,
    defaultCalendarDimension,
    currentSavedSettings,
    settingsModifiedForFavourites,
  } = context?.state as IBulkExportContextState;
  const selectedFavouriteItemId = item && "id" in item ? item?.id : undefined;
  const [selectedHierarchyIdentifier, updateCurrentHierarchyIdentifier] = useState<string | undefined>(undefined);

  const cubeId = dataSources?.find((ds) => ds.id === dataSourceId)?.identifier;
  const dataSourceItem = dataSources?.find((ds) => ds.identifier === cubeId);
  const isSavedBulk = item && "mark" in item && item?.mark?.parameters?.settingType === 0;
  const bulkId = item && "name" in item ? dataSources?.find((el) => el?.name === item?.name?.split(": ")[1])?.id : undefined;
  const favoriteBulkItemByQuery = dataFavouriteItems?.find((f) => f.id === favoriteBulkId);

  let fetchedDimensions;
  if (bulkId) {
    fetchedDimensions = useDimensionssQuery(bulkId)?.dimensions;
  } else if (tab) {
    const cubeId = savedExportSettings?.find((el) => el.id === savedExportSettingsId)?.cubeId;
    const dataSource = dataSources?.find((el) => el.identifier === cubeId);
    fetchedDimensions = useDimensionssQuery(dataSource?.id)?.dimensions;
  } else if (isSubscriptionBulk && itemCubeId) {
    const subSourceId = dataSources?.find((ds) => ds.identifier === itemCubeId)?.id!;
    fetchedDimensions = subSourceId && useDimensionssQuery(subSourceId).dimensions;
  } else {
    fetchedDimensions = useDimensionssQuery(dataSourceId).dimensions;
  }

  useEffect(() => {
    if (tab === 0 && !item && dimensions.length > 0) {
      const currentDimensions = dimensions
        .filter((dim) => !compareExeptions.includes(dim?.id))
        .map((dim) => dim?.id)
        .sort();
      const hierarchy = fetchedDimensions?.map((dim) => dim.hierarchyIdentifier).filter((dim) => !compareExeptions.includes(dim));
      const hierarchyIdentifiers = Array.isArray(hierarchy) && [...hierarchy, "virtual.configuration"].sort();
      const isDimensionsModified = isEqual(currentDimensions, hierarchyIdentifiers);
      setSettingsModifiedForFavourites(isDimensionsModified);
    }
  }, [dimensions, dataSourceId, fetchedDimensions, tab, item]);

  useEffect(() => {
    if (tab === 1 && !item) {
      const savedDimensions =
        currentSavedSettings?.dimensions
          .filter((dim) => !compareExeptions.includes(dim.id))
          .map((dim) => dim.id)
          .sort() || [];
      const currentDimensions = dimensions
        .filter((dim) => !compareExeptions.includes(dim.id))
        .map((dim) => dim.id)
        .sort();
      const isDimensionsModified = isEqual([...savedDimensions, "virtual.configuration"].sort(), currentDimensions.sort());

      if (settingsModifiedForFavourites !== isDimensionsModified) {
        setSettingsModifiedForFavourites(isDimensionsModified);
      }
    }
  }, [tab, item, JSON.stringify(dimensions), JSON.stringify(currentSavedSettings?.dimensions), settingsModifiedForFavourites]);

  useEffect(() => {
    if (favoriteBulkId) {
      const favouriteItem = dataFavouriteItems.find((f) => f.id === favoriteBulkId);
      const bulkId = dataSources?.find((el) => el?.name === favouriteItem?.name.split(": ")[1])?.id;
      if (bulkId) {
        setDataSourceId(bulkId);
      }
    }
  }, [setDataSourceId, favoriteBulkId, dataSourceId, dataSources, dataFavouriteItems]);

  useEffect(() => {
    if (isSubscriptionBulk && itemCubeId) {
      const subSourceId = dataSources?.find((ds) => ds.identifier === itemCubeId)?.id!;
      setDataSourceId(subSourceId);
    }
    if (dataSourceItem && !isSubscriptionBulk) {
      setDataSourceId(dataSourceItem?.id);
    }
  }, [setDataSourceId, dataSourceItem, isSubscriptionBulk, dataSources, itemCubeId]);

  useEffect(() => {
    const setSavedTab = () => {
      setTab && setTab(1);
    };
    if (favoriteSavedId && tab === 0) {
      setSavedTab();
    }
  }, [favoriteSavedId, setTab]);

  useEffect(() => {
    if (favoriteSavedId && setDataSourceId && setSavedExportSettingsId && tab === 1) {
      const favouriteItem = dataFavouriteItems.find((f) => f.id === favoriteSavedId);
      const dataSourceItem = dataSources?.find((source) => source.identifier === favouriteItem?.mark?.parameters?.cubeId);

      setDataSourceId(dataSourceItem?.id);
      setSavedExportSettingsId({
        id: favouriteItem?.mark.id,
        dimensions: favouriteItem?.mark?.parameters?.dimensions! as IDimension[],
        format: (favouriteItem?.mark?.parameters?.format! as number) === 0 ? BulkExportFormat.XLSX : BulkExportFormat.CSV,
      });
    }
  }, [setDataSourceId, setSavedExportSettingsId, favoriteSavedId, dataFavouriteItems, dataSources, tab]);

  useEffect(() => {
    if (currentFavouriteSettings) {
      setCurrentFavouriteItem?.(currentFavouriteSettings);
    }
  }, [
    setCurrentFavouriteItem,
    currentFavouriteSettings,
    currentFavouriteSettings?.mark?.parameters?.dimensions,
    currentFavouriteSettings?.mark?.parameters?.format,
  ]);

  useEffect(() => {
    if (favoriteBulkItemByQuery) {
      setCurrentFavouriteSettings?.(favoriteBulkItemByQuery);
    } else if (bulkType === BulkExportType.Favourite) {
      setCurrentFavouriteSettings?.(item);
    }
  }, [setCurrentFavouriteSettings, item, favoriteBulkItemByQuery, bulkType]);

  useEffect(() => {
    const visibleDimensions = fetchedDimensions?.filter((it) => it.visible);
    if (visibleDimensions?.length) {
      tab
        ? updateCurrentHierarchyIdentifier(steps[steps?.length - 1].identifier)
        : updateCurrentHierarchyIdentifier(visibleDimensions[0].hierarchyIdentifier);
    } else {
      updateCurrentHierarchyIdentifier(undefined);
    }
  }, [fetchedDimensions, tab, savedExportSettingsId, selectedFavouriteItemId]);

  useEffect(() => {
    if (favoriteBulkItemByQuery) {
      const configuration: string[] = [];
      const parameters = favoriteBulkItemByQuery?.mark?.parameters!;
      parameters?.dimensions.forEach((dimension) => {
        let dim = dimension;
        if (dimension?.id === "calendar.default") {
          dim = {
            ...dimension,
            fieldName: "Date",
          };
        }
        setDimensionCurrentFavourite?.(dim);
        if (dimension.isSelected) {
          configuration.push(`${dimension.id}.name`);
        }
      });
      parameters?.measures.forEach((dimention) => {
        configuration.push(`${dimention.id}`);
      });
      setDimensionCurrentFavourite?.({ fieldName: "", id: virtualConfiguration.identifier, isSelected: true, selection: configuration });
    }
  }, [favoriteBulkItemByQuery, setDimensionCurrentFavourite, favoriteBulkItemByQuery, favoriteBulkId, currentFavouriteSettings?.id]);

  const currentFavourite =
    item && "id" in item && dataFavouriteItems.filter((f) => f?.mark?.parameters?.settingType === 1).find((f) => f?.id === item?.id);
  useEffect(() => {
    if (currentFavourite && item?.id && currentFavourite?.mark?.parameters?.settingType === 1) {
      const configuration: string[] = [];
      const parameters = currentFavourite?.mark?.parameters!;
      parameters?.dimensions.forEach((dimention) => {
        setDimensionCurrentFavourite?.(dimention);
        if (dimention.isSelected) {
          configuration.push(`${dimention.id}.name`);
        }
      });
      parameters?.measures.forEach((dimention) => {
        configuration.push(`${dimention.id}`);
      });
      setDimensionCurrentFavourite?.({ fieldName: "", id: virtualConfiguration.identifier, isSelected: true, selection: configuration });
    }
  }, [currentFavourite, item, setDimensionCurrentFavourite]);
  useEffect(() => {
    if (item && "cubeId" in item && item?.cubeId && isSubscriptionBulk) {
      setCubeId(item?.cubeId);
    }
    if (cubeId && !isSubscriptionBulk) {
      setCubeId(cubeId);
    }
  }, [setCubeId, cubeId, isSubscriptionBulk, item]);

  useEffect(() => {
    if (
      isSubscriptionBulk &&
      applyPayload &&
      initialPayload &&
      "dimensions" in initialPayload &&
      (dimensions?.length === 0 || dimensions?.length === 1)
    ) {
      applyPayload(initialPayload);
    }
  }, [isSubscriptionBulk, applyPayload, initialPayload]);

  useEffect(() => {
    if (tab === 0 && !bulkId && !defaultCalendarDimension && !isSubscriptionBulk) {
      const defaultCalendarDimension = dimensions.find((d) => d.id === "calendar.default");
      if (defaultCalendarDimension) {
        setDefaultCalendarDimension(defaultCalendarDimension);
      }
    }
  }, [tab, bulkId, dimensions, isSubscriptionBulk]);

  const steps = useMemo(() => createSteps(fetchedDimensions), [fetchedDimensions]);
  const handleToggleStep = useCallback(
    (hierarchyIdentifier) => {
      updateCurrentHierarchyIdentifier((state) => (state === hierarchyIdentifier ? undefined : hierarchyIdentifier));
    },
    [updateCurrentHierarchyIdentifier]
  );

  const handleChangeStepByIndex = useCallback(
    (index: number) => {
      const [nextStep] = steps.slice(index);
      updateCurrentHierarchyIdentifier(nextStep?.hierarchyIdentifier);
    },
    [steps, updateCurrentHierarchyIdentifier]
  );
  return (
    <div className={classes.root}>
      {steps.map((step, index) => (
        <Step
          {...step}
          bulkId={bulkId}
          isSubscriptionBulk={isSubscriptionBulk}
          favoriteBulkItemByQuery={favoriteBulkItemByQuery}
          isSavedBulk={isSavedBulk}
          item={item}
          key={`${dataSourceId}/${step.hierarchyIdentifier}`}
          index={index}
          allSteps={steps}
          open={step.hierarchyIdentifier === selectedHierarchyIdentifier}
          onToggle={handleToggleStep}
          onChangeStep={handleChangeStepByIndex}
        />
      ))}
    </div>
  );
};
